import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LegalNotice } from '../components/LegalNotice';
import { SPLASH_FLAG } from '../components/BrandSplash';
import { useAuth } from '../context/AuthContext';
import { GoogleSignInButton, OrDivider } from '../components/auth/GoogleSignInButton';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Lock, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { AuthBackground } from '../components/auth/AuthBackground';
import { AuthLogo } from '../components/auth/AuthLogo';
import { CreditedFundsIndicator } from '../components/CreditedFundsIndicator';
import { AccountDisabledNotice } from './AccountDisabledPage';

export const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [disabledInfo, setDisabledInfo] = useState(null);
    const { login, verify2fa } = useAuth();
    const navigate = useNavigate();

    // 2FA step
    const [twoFA, setTwoFA] = useState(null); // { challenge_id, email }
    const [code, setCode] = useState('');

    const finishLogin = (result) => {
        if (result.must_change_password) {
            toast.info('Por motivos de seguridad, debe actualizar su contraseña antes de continuar.');
            navigate('/force-password-change', { replace: true });
        } else {
            toast.success('¡Bienvenido de nuevo!');
            sessionStorage.setItem(SPLASH_FLAG, '1');
            navigate('/dashboard');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const result = await login(email, password);
        if (result.requires_2fa) {
            setTwoFA({ challenge_id: result.challenge_id, email: result.email });
            toast.info('Le enviamos un código de verificación a su correo.');
        } else if (result.success) {
            finishLogin(result);
        } else if (result.error?.code === 'ACCOUNT_DISABLED') {
            setDisabledInfo(result.error);
        } else {
            toast.error(typeof result.error === 'string' ? result.error : 'No se pudo iniciar sesión');
        }
        setLoading(false);
    };

    const handleVerify2fa = async (e) => {
        e.preventDefault();
        setLoading(true);
        const result = await verify2fa(twoFA.challenge_id, code.trim());
        if (result.success) {
            finishLogin(result);
        } else if (result.error?.code === 'ACCOUNT_DISABLED') {
            setDisabledInfo(result.error);
        } else {
            toast.error(typeof result.error === 'string' ? result.error : 'No se pudo verificar el código');
        }
        setLoading(false);
    };

    if (disabledInfo) {
        return (
            <div className="relative min-h-screen flex items-center justify-center p-4 bg-black">
                <AuthBackground />
                <div className="relative w-full" style={{ zIndex: 10 }}>
                    <AccountDisabledNotice info={disabledInfo} onBack={() => setDisabledInfo(null)} />
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-screen flex items-center justify-center p-4 bg-black">
            <AuthBackground />

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative w-full max-w-md"
                style={{ zIndex: 10 }}
            >
                {/* Logo */}
                <div className="mb-8">
                    <AuthLogo />
                </div>

                <Card className="bg-slate-900/60 backdrop-blur-2xl border-slate-800/80 shadow-2xl shadow-black/40 ring-1 ring-white/5">
                    <CardHeader className="text-center">
                        <CardTitle className="text-2xl text-white" style={{ fontWeight: 700, letterSpacing: '-0.02em' }}>Bienvenido</CardTitle>
                        <CardDescription className="text-slate-400 font-light">
                            Inicia sesión para acceder a tus cuentas
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {twoFA ? (
                        <form onSubmit={handleVerify2fa} className="space-y-6" data-testid="twofa-form">
                            <div className="text-center space-y-1">
                                <p className="text-slate-300 text-sm">Verificación en dos pasos</p>
                                <p className="text-slate-500 text-xs">Introduzca el código de 6 dígitos enviado a <span className="text-slate-300">{twoFA.email}</span></p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300 font-normal">Código de verificación</Label>
                                <Input
                                    value={code}
                                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    placeholder="000000"
                                    inputMode="numeric"
                                    className="text-center text-2xl tracking-[0.5em] font-mono bg-slate-950/50 border-slate-800 text-white h-14"
                                    required
                                    data-testid="twofa-code-input"
                                />
                            </div>
                            <Button type="submit" disabled={loading || code.length !== 6}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
                                data-testid="twofa-submit-btn">
                                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verificando...</> : 'Verificar y entrar'}
                            </Button>
                            <button type="button" onClick={() => { setTwoFA(null); setCode(''); }}
                                className="w-full text-slate-400 hover:text-slate-200 text-xs" data-testid="twofa-back-btn">
                                Volver al inicio de sesión
                            </button>
                        </form>
                        ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-300 font-normal">Email</Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <Input
                                        id="email"
                                        type="email"
                                        placeholder="Ingresa tu email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="pl-10 bg-slate-950/50 border-slate-800 focus:border-[#1973B8] focus:ring-1 focus:ring-[#1973B8]/40 text-white placeholder:text-slate-600"
                                        required
                                        data-testid="login-email-input"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-300 font-normal">Contraseña</Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Ingresa tu contraseña"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="pl-10 pr-10 bg-slate-950/50 border-slate-800 focus:border-[#1973B8] focus:ring-1 focus:ring-[#1973B8]/40 text-white placeholder:text-slate-600"
                                        required
                                        data-testid="login-password-input"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                                        data-testid="toggle-password-visibility"
                                    >
                                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                    </button>
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-[0_0_15px_rgba(25,115,184,0.4)] transition-shadow hover:shadow-[0_0_25px_rgba(25,115,184,0.5)]"
                                style={{ fontWeight: 500 }}
                                data-testid="login-submit-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Iniciando sesión...
                                    </>
                                ) : (
                                    'Iniciar Sesión'
                                )}
                            </Button>

                            <div className="text-right">
                                <Link
                                    to="/forgot-password"
                                    className="text-sm text-slate-500 hover:text-emerald-400 transition-colors font-normal"
                                    data-testid="forgot-password-link"
                                >
                                    ¿Olvidaste tu contraseña?
                                </Link>
                            </div>
                        </form>
                        )}

                        {!twoFA && (
                            <>
                                <OrDivider />
                                <GoogleSignInButton />
                            </>
                        )}

                        <div className="mt-6 text-center">
                            <p className="text-slate-500 font-light">
                                ¿No tienes cuenta?{' '}
                                <Link
                                    to="/register"
                                    className="text-emerald-400 hover:text-emerald-300 transition-colors"
                                    style={{ fontWeight: 500 }}
                                    data-testid="register-link"
                                >
                                    Crear una
                                </Link>
                            </p>
                            <p className="mt-3">
                                <Link
                                    to="/legal"
                                    className="text-slate-500 hover:text-slate-300 text-xs transition-colors underline underline-offset-4"
                                    data-testid="legal-link"
                                >
                                    Aviso legal · Sobre la entidad
                                </Link>
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Public credited funds indicator */}
                <CreditedFundsIndicator variant="auth" />

                {/* Legal Disclaimer */}
                <LegalNotice compact className="mt-6" />
            </motion.div>
        </div>
    );
};

export default LoginPage;
